import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import PropTypes from 'prop-types';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { useConfirmDialog } from "../../utils/ConfirmDialog/ConfirmDialogProvider";
import InputText from "../../components/InputText";

function PartialPaymentDialog({ visible, onHide, paymentData, paymentTrackingData, onConfirm }) {
    const [partialAmount, setPartialAmount] = useState("");
    const [numberOfInstallments, setNumberOfInstallments] = useState(1);
    const [installmentDates, setInstallmentDates] = useState([null]);
    const [calculatedInstallments, setCalculatedInstallments] = useState([]);
    const [showCalculation, setShowCalculation] = useState(false);
    const [interestRate, setInterestRate] = useState("");
    const [originalInterestRate, setOriginalInterestRate] = useState("");

    // Ödeme türü ve faiz için yeni state'ler
    const [paymentType, setPaymentType] = useState('regular');
    const [paymentStatus, setPaymentStatus] = useState('ontime');
    const [daysEarlyOrLate, setDaysEarlyOrLate] = useState(0);
    const [latePaymentInterest, setLatePaymentInterest] = useState(0);
    const [earlyPaymentDiscount, setEarlyPaymentDiscount] = useState(0);
    const [manualOverride, setManualOverride] = useState(false);
    const [amountToPayAfterAdjustments, setAmountToPayAfterAdjustments] = useState(0);

    const toast = useRef(null);
    const { showDialog } = useConfirmDialog();

    const handleHide = () => {
        resetForm();
        onHide();
    };

    // ... (tüm useEffect'ler ve fonksiyonlar aynı kalacak) ...

    // Ödeme durumunu tespit et (erken/zamanında/geç)
    useEffect(() => {
        if (!visible || !paymentData || !paymentData.installmentDate) return;

        const installmentDate = new Date(paymentData.installmentDate);
        installmentDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const diffTime = installmentDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        setDaysEarlyOrLate(Math.abs(diffDays));

        let status;
        if (diffDays > 0) {
            status = 'early';
            setPaymentType('early');
        } else if (diffDays < 0) {
            status = 'late';
            setPaymentType('late');
        } else {
            status = 'ontime';
            setPaymentType('regular');
        }

        setPaymentStatus(status);
    }, [paymentData, visible]);

    // Ödeme türüne göre faiz/indirim hesapla
    useEffect(() => {
        if (!paymentData || !visible) return;

        const installmentInterestAmount = parseFloat(paymentData.installmentInterestAmount) || 0;

        let newLateInterest = 0;
        let newDiscount = 0;

        // Erken/geç ödeme durumunda faiz/indirim hesapla
        if (paymentType === 'early' && !manualOverride) {
            const dailyInterest = installmentInterestAmount / 30;
            newDiscount = Math.min(dailyInterest * daysEarlyOrLate, installmentInterestAmount);
        } else if (paymentType === 'late' && !manualOverride) {
            const dailyInterest = installmentInterestAmount / 30;
            newLateInterest = dailyInterest * daysEarlyOrLate;
        }

        setLatePaymentInterest(newLateInterest);
        setEarlyPaymentDiscount(newDiscount);

        // Toplam ödenecek tutarı güncelle
        updateAmountToPayAfterAdjustments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentData, paymentType, daysEarlyOrLate, visible, manualOverride]);

    const updateAmountToPayAfterAdjustments = () => {
        if (!paymentData) return;

        const installmentAmount = parseFloat(paymentData.totalAmountOfInstallments) || 0;
        const adjusted = installmentAmount + latePaymentInterest - earlyPaymentDiscount;

        if (partialAmount) {
            const partial = parseFloat(partialAmount);
            if (!isNaN(partial) && partial > 0) {
                setAmountToPayAfterAdjustments(partial);
                return;
            }
        }

        setAmountToPayAfterAdjustments(adjusted);
    };

    // Kısmi ödeme tutarı değiştiğinde
    useEffect(() => {
        updateAmountToPayAfterAdjustments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partialAmount, latePaymentInterest, earlyPaymentDiscount]);

    useEffect(() => {
        // Reset form when dialog opens with new payment data
        if (visible && paymentData && paymentTrackingData) {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, paymentData, paymentTrackingData]);

    const resetForm = () => {
        setPartialAmount(null);
        setNumberOfInstallments(1);
        setInstallmentDates([null]);
        setCalculatedInstallments([]);
        setShowCalculation(false);
        setManualOverride(false);
        setLatePaymentInterest(0);
        setEarlyPaymentDiscount(0);

        // Set interest rate from paymentTrackingData
        const rate = paymentTrackingData?.rate || null;
        setInterestRate(rate);
        setOriginalInterestRate(rate);
    };

    useEffect(() => {
        // Update installment dates array when number of installments changes
        const newDates = [...installmentDates];
        if (numberOfInstallments > newDates.length) {
            // Add new dates
            for (let i = newDates.length; i < numberOfInstallments; i++) {
                newDates.push(null);
            }
        } else if (numberOfInstallments < newDates.length) {
            // Remove extra dates
            newDates.splice(numberOfInstallments);
        }
        setInstallmentDates(newDates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numberOfInstallments]);

    // Calculate remaining amount based on partial amount
    const getRemainingAmount = () => {
        if (!paymentData || !partialAmount) return null;

        // Ödeme tipine göre toplam tutarı hesapla
        let totalAmount = parseFloat(paymentData.totalAmountOfInstallments);

        // Ödeme tipine göre ayarlama yap
        if (paymentType === 'late') {
            totalAmount += latePaymentInterest;
        } else if (paymentType === 'early') {
            totalAmount -= earlyPaymentDiscount;
        }

        const partial = parseFloat(partialAmount);

        if (isNaN(partial) || partial <= 0 || partial >= totalAmount) return null;

        return totalAmount - partial;
    };

    const calculateRemainingPayment = () => {
        if (!paymentData) return;

        // Parse the partial amount
        const partialAmountValue = parseFloat(partialAmount);

        // Validate inputs
        if (isNaN(partialAmountValue) || partialAmountValue <= 0) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Geçerli bir ödeme tutarı giriniz.",
                life: 3000,
            });
            return;
        }

        // Ödeme tipine göre toplam tutarı hesapla
        let totalInstallmentAmount = parseFloat(paymentData.totalAmountOfInstallments);

        if (paymentType === 'late') {
            totalInstallmentAmount += latePaymentInterest;
        } else if (paymentType === 'early') {
            totalInstallmentAmount -= earlyPaymentDiscount;
        }

        // Check if partial amount is greater than the adjusted total installment amount
        if (partialAmountValue >= totalInstallmentAmount) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Parçalı ödeme tutarı, toplam taksit tutarından küçük olmalıdır.",
                life: 3000,
            });
            return;
        }

        // Validate interest rate
        const interestRateValue = parseFloat(interestRate);
        if (isNaN(interestRateValue) || interestRateValue < 0) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Geçerli bir faiz oranı giriniz.",
                life: 3000,
            });
            return;
        }

        // Check if all installment dates are selected
        if (installmentDates.some(date => !date)) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Tüm taksit tarihlerini seçiniz.",
                life: 3000,
            });
            return;
        }

        // Sort dates in ascending order
        const sortedDates = [...installmentDates].sort((a, b) => new Date(a) - new Date(b));

        // Check if any date is before today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (sortedDates.some(date => new Date(date) < today)) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Taksit tarihleri bugünden sonra olmalıdır.",
                life: 3000,
            });
            return;
        }

        // Calculate remaining amount (after partial payment) based on adjusted total
        const remainingAmount = totalInstallmentAmount - partialAmountValue;

        // Get monthly interest rate from user input
        const monthlyRate = interestRateValue / 100;
        const dailyRate = monthlyRate / 30; // Convert monthly rate to daily rate

        // Calculate new installments with interest
        const newInstallments = calculateNewInstallments(
            remainingAmount,
            sortedDates,
            dailyRate,
            paymentData
        );

        setCalculatedInstallments(newInstallments);
        setShowCalculation(true);
    };

    const calculateNewInstallments = (remainingAmount, dates, dailyRate, paymentData) => {
        const installments = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Calculate total days and total interest
        let totalInterest = 0;

        // For each installment date, calculate the interest based on days from today
        dates.forEach((date, index) => {
            const installmentDate = new Date(date);
            const daysDiff = Math.round((installmentDate - today) / (1000 * 60 * 60 * 24));
            const interest = remainingAmount * dailyRate * daysDiff;
            totalInterest += interest;
        });

        // Calculate principal per installment (equal distribution)
        const principalPerInstallment = remainingAmount / dates.length;

        // Create installment objects
        let remainingPrincipal = remainingAmount;
        let remainingInterest = totalInterest;

        dates.forEach((date, index) => {
            const installmentDate = new Date(date);
            const daysDiff = Math.round((installmentDate - today) / (1000 * 60 * 60 * 24));

            // For the last installment, use remaining values to avoid rounding issues
            const isLastInstallment = index === dates.length - 1;

            const principal = isLastInstallment ? remainingPrincipal : principalPerInstallment;
            const interest = isLastInstallment ? remainingInterest : (totalInterest / dates.length);
            const amount = principal + interest;

            remainingPrincipal -= principal;
            remainingInterest -= interest;

            installments.push({
                installmentOrder: index + 1,
                installmentDate: installmentDate.toISOString(),
                totalAmountOfInstallments: parseFloat(amount.toFixed(2)),
                installmentPrincipalAmount: parseFloat(principal.toFixed(2)),
                installmentInterestAmount: parseFloat(interest.toFixed(2)),
                daysDiff: daysDiff,
                isPaid: { id: 0, value: "Tamamlanmadı" }
            });
        });

        return installments;
    };

    const handleConfirm = () => {
        if (!paymentData || !showCalculation) return;

        // Prepare data for API
        const partialPaymentData = {
            // Original payment data
            paymentTrackingId: paymentData.paymentTrackingId,
            originalInstallmentId: paymentData.id,
            originalAmount: parseFloat(paymentData.totalAmountOfInstallments),

            // Parent payment tracking data
            parentData: paymentTrackingData, // Üst tablo verisi

            // Partial payment info
            partialAmount: parseFloat(partialAmount),
            interestRate: parseFloat(interestRate), // Kullanıcının belirlediği faiz oranı

            // Ödeme türü ve faiz bilgileri
            paymentType: paymentType,
            latePaymentInterest: parseFloat(latePaymentInterest.toFixed(2)),
            earlyPaymentDiscount: parseFloat(earlyPaymentDiscount.toFixed(2)),
            amountToPayAfterAdjustments: parseFloat(amountToPayAfterAdjustments.toFixed(2)),

            // New installments
            newInstallments: calculatedInstallments
        };

        onConfirm(partialPaymentData);
        resetForm();
    };

    const formatCurrency = (value) => {
        return parseFloat(value).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handlePaymentTypeChange = (value) => {
        setPaymentType(value);
        setManualOverride(false);

        if (value === 'early') {
            setLatePaymentInterest(0);
        } else if (value === 'late') {
            setEarlyPaymentDiscount(0);
        } else if (value === 'regular') {
            setEarlyPaymentDiscount(0);
            setLatePaymentInterest(0);
        }
    };

    const getStatusMessage = () => {
        if (paymentStatus === 'early') {
            return `Taksit tarihinden ${daysEarlyOrLate} gün önce ödeme yapıyorsunuz. Erken ödeme indirimi uygulanabilir.`;
        } else if (paymentStatus === 'late') {
            return `Taksit tarihinden ${daysEarlyOrLate} gün sonra ödeme yapıyorsunuz. Gecikme faizi uygulanabilir.`;
        } else {
            return 'Taksit tarihinde ödeme yapıyorsunuz.';
        }
    };

    // Ödeme tipine göre toplam tutarı hesapla
    const getAdjustedTotalAmount = () => {
        if (!paymentData) return 0;

        let totalAmount = parseFloat(paymentData.totalAmountOfInstallments) || 0;

        if (paymentType === 'late') {
            totalAmount += latePaymentInterest;
        } else if (paymentType === 'early') {
            totalAmount -= earlyPaymentDiscount;
        }

        return totalAmount;
    };

    if (!visible || !paymentData) return null;

    const remainingAmount = getRemainingAmount();
    const adjustedTotalAmount = getAdjustedTotalAmount();

    return (
        <Dialog
            header="Parçalı Ödeme"
            visible={visible}
            onHide={handleHide}
            style={{ maxHeight: "95vh", width: "95vw", maxWidth: "1200px" }}
            className="payment-dialog bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-0 shadow-lg"
            contentClassName="p-4 bg-white dark:bg-gray-900 overflow-auto"
            headerClassName="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700"
            footerClassName="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
            footer={
                showCalculation && calculatedInstallments.length > 0 ? (
                    <div className="flex justify-between items-center gap-4 w-full">
                        <div className="bg-green-50 dark:bg-green-900 p-3 rounded-lg border border-green-200 dark:border-green-800 flex-1">
                            <p className="text-green-800 dark:text-green-200 text-sm flex items-start">
                                <i className="pi pi-info-circle mr-2 mt-0.5"></i>
                                <span>
                                    <strong>₺{formatCurrency(parseFloat(partialAmount))}</strong> tutarında ödeme
                                    {paymentType === 'early' && earlyPaymentDiscount > 0 && (
                                        <span> (<strong>₺{formatCurrency(earlyPaymentDiscount)}</strong> erken ödeme indirimi ile)</span>
                                    )}
                                    {paymentType === 'late' && latePaymentInterest > 0 && (
                                        <span> (<strong>₺{formatCurrency(latePaymentInterest)}</strong> gecikme faizi ile)</span>
                                    )}
                                    {" "}yapılacak ve <strong>₺{formatCurrency(remainingAmount)}</strong> tutarındaki kalan kısım
                                    için <strong>{numberOfInstallments}</strong> adet yeni taksit oluşturulacaktır.
                                </span>
                            </p>
                        </div>
                        <div className="flex-rol gap-3">
                            <Button
                                label="İptal"
                                icon="pi pi-times"
                                onClick={handleHide}
                                className="border rounded-xl shadow-lg cursor-pointer text-red-700 dark:text-red-400 hover:text-white dark:hover:text-white border-red-700 dark:border-red-500 hover:bg-red-800 dark:hover:bg-red-700 p-2"
                            />
                            <Button
                                label="Parçalı Ödemeyi Tamamla"
                                icon="pi pi-check"
                                onClick={() => showDialog({
                                    message: "Parçalı ödemeyi kaydetmek istediğinize emin misiniz ?",
                                    onConfirm: () => {
                                        handleConfirm();
                                    }
                                })}
                                className="rounded-xl shadow-lg cursor-pointer text-white bg-green-700 dark:bg-green-800 hover:bg-green-800 dark:hover:bg-green-700 p-2"
                            />
                        </div>
                    </div>
                ) : null
            }
        >
            <Toast ref={toast} />

            {paymentTrackingData && (
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                        Müşteri Bilgileri
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="text-gray-600 dark:text-gray-300">Müşteri:</span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">
                                    {paymentTrackingData.name} {paymentTrackingData.surname}
                                </span>
                            </div>
                            {paymentTrackingData.guarantorName && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-300">Kefil:</span>
                                    <span className="font-medium text-gray-800 dark:text-gray-100">
                                        {paymentTrackingData.guarantorName} {paymentTrackingData.guarantorSurname}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="text-gray-600 dark:text-gray-300">Toplam Tutar:</span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">
                                    ₺{formatCurrency(paymentTrackingData.totalRefundAmount || 0)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-300">Vade:</span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">
                                    {paymentTrackingData.term} Ay
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                        Taksit Bilgileri
                    </h3>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Taksit No:</span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                                {paymentData.installmentOrder}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Taksit Tarihi:</span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                                {paymentData.installmentDate ? new Date(paymentData.installmentDate).toLocaleDateString('tr-TR') : ''}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Taksit Tutarı:</span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                                ₺{formatCurrency(paymentData.totalAmountOfInstallments)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Anapara:</span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                                ₺{formatCurrency(paymentData.installmentPrincipalAmount)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-300">Faiz:</span>
                            <span className="font-medium text-gray-800 dark:text-gray-100">
                                ₺{formatCurrency(paymentData.installmentInterestAmount)}
                            </span>
                        </div>

                        <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-md">
                            <p className="text-sm text-gray-700 dark:text-gray-300">{getStatusMessage()}</p>
                        </div>
                    </div>

                    {/* Ödeme Türü Bölümü */}
                    <div className="mt-4 border-t border-gray-200 dark:border-gray-600 pt-4">
                        <h4 className="font-semibold mb-2 text-gray-800 dark:text-gray-100">Ödeme Türü</h4>
                        <div className="flex flex-col gap-2">
                            {paymentStatus === 'early' && (
                                <div className="flex items-center">
                                    <RadioButton
                                        inputId="early"
                                        name="paymentType"
                                        value="early"
                                        onChange={e => handlePaymentTypeChange(e.value)}
                                        checked={paymentType === 'early'}
                                        className="mr-2"
                                    />
                                    <label htmlFor="early" className="text-gray-700 dark:text-gray-300 ml-1">
                                        Erken Ödeme <span className="text-green-500 dark:text-green-400">(İndirim Uygulanır)</span>
                                    </label>
                                </div>
                            )}
                            <div className="flex items-center">
                                <RadioButton
                                    inputId="regular"
                                    name="paymentType"
                                    value="regular"
                                    onChange={e => handlePaymentTypeChange(e.value)}
                                    checked={paymentType === 'regular'}
                                    className="mr-2"
                                />
                                <label htmlFor="regular" className="text-gray-700 dark:text-gray-300 ml-1">Normal Ödeme</label>
                            </div>
                            {paymentStatus === 'late' && (
                                <div className="flex items-center">
                                    <RadioButton
                                        inputId="late"
                                        name="paymentType"
                                        value="late"
                                        onChange={e => handlePaymentTypeChange(e.value)}
                                        checked={paymentType === 'late'}
                                        className="mr-2"
                                    />
                                    <label htmlFor="late" className="text-gray-700 dark:text-gray-300 ml-1">
                                        Geç Ödeme <span className="text-red-500 dark:text-red-400">(Ek Faiz Uygulanır)</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Ödeme Detayları */}
                    <div className="mt-4 border-t border-gray-200 dark:border-gray-600 pt-4">
                        <div className="flex items-center mb-3">
                            <h4 className="font-semibold flex-grow text-gray-800 dark:text-gray-100">Ödeme Detayları</h4>
                            <div className="flex items-center">
                                <Checkbox
                                    inputId="manualOverride"
                                    checked={manualOverride}
                                    onChange={e => setManualOverride(e.checked)}
                                    className="mr-2"
                                />
                                <label htmlFor="manualOverride" className="text-sm text-gray-600 dark:text-gray-400 ml-1">Manuel Düzenleme</label>
                            </div>
                        </div>

                        {(paymentType === 'late' || (manualOverride && paymentType !== 'early')) && (
                            <div className="mb-3">
                                <InputText
                                    name="latePaymentInterest"
                                    type="number"
                                    label="Gecikme Faizi"
                                    value={latePaymentInterest}
                                    onChange={(e) => {
                                        setManualOverride(true);
                                        setLatePaymentInterest(e.target.value || 0);
                                    }}
                                    clearFunction={() => setLatePaymentInterest(0)}
                                    min={0}
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    currency="TRY"
                                    disabled={!manualOverride && paymentType !== 'late'}
                                />
                            </div>
                        )}

                        {(paymentType === 'early' || (manualOverride && paymentType !== 'late')) && (
                            <div className="my-6">
                                <InputText
                                    name="earlyPaymentDiscount"
                                    type="number"
                                    label="Erken Ödeme İndirimi"
                                    value={earlyPaymentDiscount}
                                    onChange={(e) => {
                                        setManualOverride(true);
                                        const maxDiscount = parseFloat(paymentData.installmentInterestAmount) || 0;
                                        const discountValue = Math.min(e.target.value || 0, maxDiscount);
                                        setEarlyPaymentDiscount(discountValue);
                                    }}
                                    clearFunction={() => setEarlyPaymentDiscount(0)}
                                    required
                                    min={0}
                                    disabled={!manualOverride && paymentType !== 'early'}
                                    max={parseFloat(paymentData.installmentInterestAmount) || 0}
                                    currency="TRY"
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                        Parçalı Ödeme Bilgileri
                    </h3>
                    <div className="space-y-4">
                        <div className="flex flex-col">
                            <InputText
                                name="partialAmount"
                                type="number"
                                label="Ödenecek Tutar"
                                value={partialAmount}
                                onChange={(e) => setPartialAmount(e.target.value)}
                                clearFunction={() => setPartialAmount(null)}
                                required
                                min={0}
                                minFractionDigits={2}
                                maxFractionDigits={2}
                                currency="TRY"
                            />
                        </div>

                        {/* Özet bölümü */}
                        <div className="p-3 bg-blue-50 dark:bg-blue-900 rounded-md border border-blue-200 dark:border-blue-800">
                            <h4 className="font-semibold mb-2 text-blue-700 dark:text-blue-300">Ödeme Özeti</h4>
                            <div className="flex justify-between mb-1">
                                <span className="text-blue-700 dark:text-blue-300">Taksit Tutarı:</span>
                                <span className="font-medium text-blue-800 dark:text-blue-200">
                                    ₺{formatCurrency(paymentData.totalAmountOfInstallments)}
                                </span>
                            </div>

                            {latePaymentInterest > 0 && (
                                <div className="flex justify-between mb-1">
                                    <span className="text-blue-700 dark:text-blue-300">Gecikme Faizi:</span>
                                    <span className="font-medium text-red-600 dark:text-red-400">
                                        +₺{formatCurrency(latePaymentInterest)}
                                    </span>
                                </div>
                            )}

                            {earlyPaymentDiscount > 0 && (
                                <div className="flex justify-between mb-1">
                                    <span className="text-blue-700 dark:text-blue-300">Erken Ödeme İndirimi:</span>
                                    <span className="font-medium text-green-600 dark:text-green-400">
                                        -₺{formatCurrency(earlyPaymentDiscount)}
                                    </span>
                                </div>
                            )}

                            <div className="flex justify-between font-semibold mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                                <span className="text-blue-700 dark:text-blue-300">Toplam:</span>
                                <span className="text-blue-800 dark:text-blue-200">
                                    ₺{formatCurrency(adjustedTotalAmount)}
                                </span>
                            </div>

                            {remainingAmount && (
                                <>
                                    <div className="flex justify-between mt-3 pt-2 border-t border-blue-200 dark:border-blue-700">
                                        <span className="text-blue-700 dark:text-blue-300 font-medium">Kısmi Ödeme:</span>
                                        <span className="text-blue-800 dark:text-blue-200 font-bold">
                                            ₺{formatCurrency(parseFloat(partialAmount))}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-blue-700 dark:text-blue-300 font-medium">Kalan Tutar:</span>
                                        <span className="text-blue-800 dark:text-blue-200 font-bold">
                                            ₺{formatCurrency(remainingAmount)}
                                        </span>
                                    </div>
                                    <p className="text-blue-600 dark:text-blue-300 text-sm mt-1">
                                        Bu kalan tutar üzerinden yeni taksitlendirme yapılacaktır.
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="flex flex-row items-center gap-4">
                            <InputText
                                name="interestRate"
                                type="number"
                                label="Faiz Oranı (% Aylık)"
                                value={interestRate}
                                onChange={(e) => setInterestRate(e.target.value)}
                                clearFunction={() => setInterestRate(null)}
                                required
                                min={0}
                                max={100}
                                step={0.01}
                                minFractionDigits={2}
                                maxFractionDigits={4}
                            />
                            <button
                                onClick={() => setInterestRate(originalInterestRate)}
                                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
                                title="Varsayılan faiz oranına sıfırla"
                            >
                                <i className="pi pi-refresh"></i>
                            </button>
                        </div>

                        <div className="flex flex-col">
                            <InputText
                                name="numberOfInstallments"
                                type="number"
                                label="Kalan Tutar İçin Taksit Sayısı"
                                value={numberOfInstallments}
                                onChange={(e) => setNumberOfInstallments(parseInt(e.target.value) || 1)}
                                clearFunction={() => setNumberOfInstallments(1)}
                                required
                                min={1}
                                max={12}
                                step={1}
                            />
                        </div>

                        <div className="space-y-3">
                            <label className="text-gray-600 dark:text-gray-300 font-medium">
                                Taksit Tarihleri
                            </label>
                            {installmentDates.map((date, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600 dark:text-gray-300 w-20">
                                        {index + 1}. Taksit:
                                    </span>
                                    <InputText
                                        name={`installmentDate_${index}`}
                                        type="date"
                                        value={date}
                                        onChange={(e) => {
                                            const newDates = [...installmentDates];
                                            newDates[index] = e.target.value;
                                            setInstallmentDates(newDates);
                                        }}
                                        clearFunction={() => {
                                            const newDates = [...installmentDates];
                                            newDates[index] = null;
                                            setInstallmentDates(newDates);
                                        }}
                                        className="flex-grow"
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={resetForm}
                                className="w-1/3 p-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors dark:bg-gray-600 dark:hover:bg-gray-700"
                            >
                                Sıfırla
                            </button>
                            <button
                                onClick={calculateRemainingPayment}
                                className="w-2/3 p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors dark:bg-blue-700 dark:hover:bg-blue-800"
                            >
                                Hesapla
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showCalculation && calculatedInstallments.length > 0 && (
                <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                        Yeni Ödeme Planı
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white dark:bg-gray-700 rounded-lg overflow-hidden">
                            <thead className="bg-gray-100 dark:bg-gray-600">
                                <tr>
                                    <th className="py-2 px-4 text-left text-gray-700 dark:text-gray-200">Taksit No</th>
                                    <th className="py-2 px-4 text-left text-gray-700 dark:text-gray-200">Tarih</th>
                                    <th className="py-2 px-4 text-left text-gray-700 dark:text-gray-200">Gün Farkı</th>
                                    <th className="py-2 px-4 text-right text-gray-700 dark:text-gray-200">Anapara</th>
                                    <th className="py-2 px-4 text-right text-gray-700 dark:text-gray-200">Faiz</th>
                                    <th className="py-2 px-4 text-right text-gray-700 dark:text-gray-200">Toplam</th>
                                </tr>
                            </thead>
                            <tbody>
                                {calculatedInstallments.map((installment, index) => (
                                    <tr key={index} className="border-t border-gray-200 dark:border-gray-600">
                                        <td className="py-2 px-4 text-gray-800 dark:text-gray-200">{installment.installmentOrder}</td>
                                        <td className="py-2 px-4 text-gray-800 dark:text-gray-200">
                                            {new Date(installment.installmentDate).toLocaleDateString('tr-TR')}
                                        </td>
                                        <td className="py-2 px-4 text-gray-800 dark:text-gray-200">{installment.daysDiff} gün</td>
                                        <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-200">
                                            ₺{formatCurrency(installment.installmentPrincipalAmount)}
                                        </td>
                                        <td className="py-2 px-4 text-right text-gray-800 dark:text-gray-200">
                                            ₺{formatCurrency(installment.installmentInterestAmount)}
                                        </td>
                                        <td className="py-2 px-4 text-right font-medium text-gray-800 dark:text-gray-200">
                                            ₺{formatCurrency(installment.totalAmountOfInstallments)}
                                        </td>
                                    </tr>
                                ))}
                                <tr className="border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-600">
                                    <td colSpan="3" className="py-2 px-4 font-semibold text-gray-800 dark:text-gray-200">Toplam</td>
                                    <td className="py-2 px-4 text-right font-semibold text-gray-800 dark:text-gray-200">
                                        ₺{formatCurrency(calculatedInstallments.reduce((sum, item) => sum + item.installmentPrincipalAmount, 0))}
                                    </td>
                                    <td className="py-2 px-4 text-right font-semibold text-gray-800 dark:text-gray-200">
                                        ₺{formatCurrency(calculatedInstallments.reduce((sum, item) => sum + item.installmentInterestAmount, 0))}
                                    </td>
                                    <td className="py-2 px-4 text-right font-semibold text-gray-800 dark:text-gray-200">
                                        ₺{formatCurrency(calculatedInstallments.reduce((sum, item) => sum + item.totalAmountOfInstallments, 0))}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </Dialog>
    );
}

PartialPaymentDialog.propTypes = {
    visible: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    paymentData: PropTypes.object,
    paymentTrackingData: PropTypes.object,
    onConfirm: PropTypes.func.isRequired
};

export default PartialPaymentDialog;